import { matchPath, useLocation } from 'react-router-dom';
import { INVOICE_DETAIL_ROUTE_PATH_LIST, MATTER_EDIT_ROUTE_PATH_LIST } from 'routes/constants';
import { EditableResourceType } from 'store/api/editingSession';
import { FeatureFlag } from 'types/features';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

interface EditableResource {
    resourceType: EditableResourceType;
    resourceId: string;
    messageId: string;
}

// Sourced from the router so the ribbon tracks exactly the URLs that render InvoicePage.
// matchPath is exact by default, so every invoice URL variant (incl. the `/:tab` routes) must
// be listed; keeping this derived from the shared constant prevents the
// router and the ribbon from silently drifting apart again.
const INVOICE_PATTERNS = INVOICE_DETAIL_ROUTE_PATH_LIST;
// Scoped to the matter edit page (not the detail view) and derived from the router for the
// same drift protection as the invoice patterns above.
const CASE_PATTERNS = MATTER_EDIT_ROUTE_PATH_LIST;
const CORRESPONDENCE_PATTERNS = ['/correspondence/:id/edit'];

const useEditableResourceFromRoute = (): EditableResource | null => {
    const { pathname } = useLocation();
    const isInvoiceListenerEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_LISTENER);
    const isCaseListenerEnabled = useIsFeatureEnabled(FeatureFlag.CASE_LISTENER);

    for (const pattern of INVOICE_PATTERNS) {
        const match = matchPath(pattern, pathname);
        if (match && isInvoiceListenerEnabled) {
            return {
                resourceType: 'INVOICE' as const,
                resourceId: match.params.id!,
                messageId: 'invoices.concurrent-editing-warning',
            };
        }
    }

    for (const pattern of CASE_PATTERNS) {
        const match = matchPath(pattern, pathname);
        if (match && isCaseListenerEnabled) {
            return {
                resourceType: 'CASE' as const,
                resourceId: match.params.id!,
                messageId: 'matters.concurrent-editing-warning',
            };
        }
    }

    for (const pattern of CORRESPONDENCE_PATTERNS) {
        const match = matchPath(pattern, pathname);
        if (match) {
            return {
                resourceType: 'CORRESPONDENCE' as const,
                resourceId: match.params.id!,
                messageId: 'correspondence.concurrent-editing-warning',
            };
        }
    }

    return null;
};

export default useEditableResourceFromRoute;
